/*
 * 修正搜索结果页的分页「幽灵空页」。
 *
 * 背景：Shopify 原生搜索的 search.results_count 可能严重虚高（实测出现过
 * 上限值 1000，而实际可渲染的结果只有几十个），导致 paginate.pages 大于
 * 真实页数，分页后面的页为空白。
 *
 * 策略（无需 Storefront API token）：
 * 1. 服务端兜底：main-search.liquid 检测当前页无结果时输出回退跳转；
 * 2. 客户端修正：利用 Section Rendering API（section_id 参数，与主题
 *    facet 筛选的刷新机制一致）按页码探测「该页是否有结果」。真实页构成
 *    前缀 1..R，据此分轮「并发」探测区间等分点（每轮至多 PROBE_BATCH_SIZE
 *    个请求），快速收敛到最后一个有结果的页码：
 *    - 当前页是幽灵页时，直接跳转到最后一个有结果的页；
 *    - 当前页是真实页时，修正分页总数、跳页上限、下一页箭头与标题计数。
 * 3. 防闪烁：脚本在 section 标记之后同步执行并立即开始修正（不等
 *    DOMContentLoaded）；探测期间对标题计数与分页导航临时 visibility:hidden
 *    （保留布局），修正值写入后立即显示；探测失败或超时则恢复原生显示，
 *    避免用户先看到虚高值再被「刷新」。
 */
(function () {
  'use strict';

  function readFixer() {
    var fixer = document.getElementById('search-pagination-fixer');
    if (!fixer) return null;

    var sectionId = fixer.getAttribute('data-section-id');
    if (!sectionId) return null;

    return {
      sectionId: sectionId,
      pageResults: parseInt(fixer.getAttribute('data-page-results'), 10) || 0,
      perPage: parseInt(fixer.getAttribute('data-per-page'), 10) || 24,
      currentPage: parseInt(fixer.getAttribute('data-current-page'), 10) || 1,
      totalPages: parseInt(fixer.getAttribute('data-total-pages'), 10) || 1
    };
  }

  function getCacheKey() {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');

    return 'search-pagination-fix:' + params.toString();
  }

  function readCache() {
    try {
      var cached = JSON.parse(sessionStorage.getItem(getCacheKey()));

      if (cached && typeof cached.realPages === 'number' && cached.realPages >= 1 && typeof cached.lastPageResults === 'number') {
        return cached;
      }
    } catch (error) {}

    return null;
  }

  function writeCache(result) {
    try {
      sessionStorage.setItem(getCacheKey(), JSON.stringify(result));
    } catch (error) {}
  }

  // 同一 URL 的探测共享同一个 Promise：分轮并发时不会重复请求同一页，
  // facet 更换查询后 URL 不同，不会误命中缓存。失败的探测移出缓存，
  // 允许后续触发时重试。
  var probePromises = new Map();

  // 通过 Section Rendering API 渲染指定页码的搜索区块，返回该页实际渲染的
  // 产品数量（-1 表示响应异常，无法判断）。
  function probePage(info, page) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', info.sectionId);

    var requestKey = url.toString();

    if (!probePromises.has(requestKey)) {
      probePromises.set(
        requestKey,
        fetch(url.toString())
          .then(function (response) {
            return response.text();
          })
          .then(function (html) {
            var fixer = new DOMParser().parseFromString(html, 'text/html').getElementById('search-pagination-fixer');

            if (!fixer) return -1;

            var count = parseInt(fixer.getAttribute('data-page-results'), 10);

            return isNaN(count) ? -1 : count;
          })
          .catch(function (error) {
            probePromises.delete(requestKey);

            throw error;
          })
      );
    }

    return probePromises.get(requestKey);
  }

  // 查找最后一个有结果的页码。knownReal 页必须确认有结果，在
  // (knownReal, upperBound] 区间内查找。真实页构成前缀 1..R，因此每轮可以把
  // 不确定区间均分、并发探测至多 PROBE_BATCH_SIZE 个等分点：一轮结果利用
  // 单调性（前段 real、后段 ghost）即可把区间压缩到约 1/(批大小+1)，
  // 墙钟时间从「逐个串行二分」的 O(log n) 次请求延迟降到约 3 轮并发。
  var PROBE_BATCH_SIZE = 4;

  function findLastRealPage(info, knownReal, upperBound, knownRealResults) {
    var lo = knownReal;      // 已确认有结果的页（realPages 下界）
    var hi = upperBound;     // realPages 可能的上界（含）
    var loResults = knownRealResults;

    function step() {
      if (lo >= hi) {
        return Promise.resolve({ realPages: lo, lastPageResults: loResults });
      }

      // 在 (lo, hi] 内取 k 个等分点并发探测
      var width = hi - lo;
      var batchSize = Math.min(PROBE_BATCH_SIZE, width);
      var pages = [];

      for (var i = 1; i <= batchSize; i++) {
        pages.push(lo + Math.ceil((i * width) / (batchSize + 1)));
      }

      return Promise.all(
        pages.map(function (page) {
          return probePage(info, page).then(function (count) {
            if (count < 0) {
              throw new Error('Unable to probe search page ' + page);
            }

            return { page: page, isReal: count > 0, count: count };
          });
        })
      ).then(function (probes) {
        var lastReal = null;
        var firstGhost = null;
        var seenGhost = false;

        for (var i = 0; i < probes.length; i++) {
          if (probes[i].isReal) {
            // 出现「ghost 之后再 real」说明结果不具备前缀单调性，数据异常
            if (seenGhost) {
              throw new Error('Inconsistent probe results around page ' + probes[i].page);
            }

            lastReal = probes[i];
          } else {
            seenGhost = true;

            if (firstGhost === null) {
              firstGhost = probes[i];
            }
          }
        }

        if (lastReal) {
          lo = lastReal.page;
          loResults = lastReal.count;
        }

        if (firstGhost) {
          hi = firstGhost.page - 1;
        }

        return step();
      });
    }

    return step();
  }

  // 构造跳转用的页面地址（去掉 section_id，确保是完整页面导航）
  function buildPageUrl(page) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', page);
    url.searchParams.delete('section_id');

    return url.toString();
  }

  // 幽灵页：探测最后一个有结果的页码并跳转过去
  function redirectFromPhantomPage(info) {
    // 优先使用同一次访问中已探测出的真实页数（必须小于当前页码，避免来回跳）
    var cached = readCache();

    if (cached && cached.realPages < info.currentPage) {
      window.location.replace(buildPageUrl(cached.realPages));
      return;
    }

    // 第 1 页不一定有结果（结果可能全部被过滤），先探测它
    probePage(info, 1)
      .then(function (countOnFirstPage) {
        if (countOnFirstPage < 0) {
          throw new Error('Unable to probe search page 1');
        }

        if (countOnFirstPage === 0) {
          // 整个搜索实际上没有可显示的结果，回到不带页码的搜索页
          var url = new URL(window.location.href);
          url.searchParams.delete('page');
          url.searchParams.delete('section_id');

          window.location.replace(url.toString());
          return;
        }

        return findLastRealPage(info, 1, info.currentPage - 1, countOnFirstPage).then(function (result) {
          writeCache(result);
          window.location.replace(buildPageUrl(result.realPages));
        });
      })
      .catch(function () {
        // 探测失败时退回上一页（服务端兜底会重新校验，必要时继续回退）
        window.location.replace(buildPageUrl(Math.max(1, info.currentPage - 1)));
      });
  }

  // —— 防闪烁：探测期间临时隐藏标题计数与分页导航 ——
  // 用 visibility 保留布局、避免跳动；修正值写入后立即恢复显示。探测失败
  // 或超时也强制恢复，绝不会让页面一直空白。
  var PENDING_STYLE_ID = 'search-pagination-fix-pending-style';
  var PENDING_TIMEOUT = 4000;
  var pendingTimeoutId = 0;

  function beginPendingUI() {
    if (!document.getElementById(PENDING_STYLE_ID)) {
      var style = document.createElement('style');

      style.id = PENDING_STYLE_ID;
      style.textContent =
        '.search-fix-pending [data-results-count],.search-fix-pending .collection__pagination{visibility:hidden}';

      document.head.appendChild(style);
    }

    var section = document.querySelector('.shopify-section--main-search');

    if (section) {
      section.classList.add('search-fix-pending');
    }

    // 兜底：无论探测结果如何，超时后必定恢复显示（例如请求长时间挂起）
    clearTimeout(pendingTimeoutId);
    pendingTimeoutId = setTimeout(endPendingUI, PENDING_TIMEOUT);
  }

  function endPendingUI() {
    clearTimeout(pendingTimeoutId);

    var section = document.querySelector('.shopify-section--main-search');

    if (section) {
      section.classList.remove('search-fix-pending');
    }
  }

  // 真实页：修正分页显示
  function fixPaginationUI(info) {
    if (info.totalPages <= info.currentPage) {
      // 分页页数没有虚高，无需修正分页导航；但 results_count 仍可能虚高。
      // 当前页即末页且未满页时，真实结果数可直接推算，无需发起探测请求。
      fixHeadingOnFinalPage(info);

      return;
    }

    var cached = readCache();
    var detection;

    if (cached && cached.realPages >= info.currentPage) {
      // 命中缓存：同步修正，无需隐藏
      detection = Promise.resolve(cached);
    } else {
      // 探测期间先隐藏虚高值，修正完成（或失败/超时）后再恢复显示
      beginPendingUI();

      detection = findLastRealPage(info, info.currentPage, info.totalPages, info.pageResults).then(function (result) {
        writeCache(result);

        return result;
      });
    }

    detection.then(function (result) {
      applyFix(info, result);
      endPendingUI();
    }).catch(function () {
      // 探测失败时保持原生分页显示（服务端兜底仍然生效）
      endPendingUI();
    });
  }

  function applyFix(info, result) {
    // 修正「当前页 / 总页数」显示
    document.querySelectorAll('[data-pagination-total]').forEach(function (element) {
      element.textContent = result.realPages;
    });

    // 同步跳页输入框的最大页码
    document.querySelectorAll('[data-pagination-jump]').forEach(function (input) {
      input.max = result.realPages;

      if (parseInt(input.value, 10) > result.realPages) {
        input.value = result.realPages;
      }
    });

    // 当前页已是最后一个有结果的页时，禁用「下一页」箭头
    if (info.currentPage >= result.realPages) {
      disableNextLink();
    }

    // 修正标题中的结果数（近似值：满页页数 x 每页数量 + 末页实际数量）
    fixResultsHeading((result.realPages - 1) * info.perPage + result.lastPageResults);
  }

  // 修正标题中的结果数。计数渲染在独立的 [data-results-count] 元素中
  // （见 main-search.liquid），避免翻译串中计数不在最前（如 zh-CN）或
  // 搜索词含数字时误改搜索词。
  function fixResultsHeading(accurateCount) {
    var countElement = document.querySelector('[data-results-count]');

    if (!countElement) return;

    var displayedCount = parseInt(countElement.textContent, 10);

    // 仅当显示值大于推算值（确系虚高）时才改写：results_count 被 Shopify
    // 截断到上限时推算值可能反而更大，保持原显示更稳妥。
    if (!isNaN(displayedCount) && displayedCount > accurateCount) {
      countElement.textContent = String(accurateCount);
    }
  }

  // 分页页数没有虚高时无法通过二分探测获取更多信息，但如果当前页就是
  // 最后一页且未满页，真实结果数可直接推算：
  // (总页数 - 1) x 每页数量 + 末页实际数量。计数虚高时借此修正标题。
  function fixHeadingOnFinalPage(info) {
    if (info.pageResults >= info.perPage) {
      return; // 满页时推算值只会大于等于显示值（页数由计数取整而来），无需处理
    }

    fixResultsHeading((info.totalPages - 1) * info.perPage + info.pageResults);
  }

  function disableNextLink() {
    var nextLink = document.querySelector('a[data-pagination-next]');

    if (!nextLink) return;

    var disabled = document.createElement('span');
    disabled.className = nextLink.className + ' pagination__item--disabled';
    disabled.innerHTML = nextLink.innerHTML;
    disabled.setAttribute('aria-disabled', 'true');
    nextLink.replaceWith(disabled);
  }

  function fixPagination() {
    var info = readFixer();

    if (!info) return;

    if (info.currentPage > 1 && info.pageResults === 0) {
      redirectFromPhantomPage(info);
    } else if (info.pageResults > 0) {
      fixPaginationUI(info);
    }
  }

  // 脚本位于 section 内容之后，执行时 fixer/分页/标题均已解析完毕，
  // 立即开始修正以免用户先看到虚高分页；若因故被移到 section 之前加载，
  // 则回退到 DOMContentLoaded 再执行。
  if (document.getElementById('search-pagination-fixer')) {
    fixPagination();
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixPagination);
  } else {
    fixPagination();
  }

  // facet 筛选会通过 Section Rendering API 重载整个 section，
  // 分页导航与 data 容器会被替换，因此重新执行修正。
  var section = document.querySelector('.shopify-section--main-search');
  if (section && window.MutationObserver) {
    var observer = new MutationObserver(function (mutations) {
      var fixerReplaced = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return node.id === 'search-pagination-fixer';
        }) || Array.prototype.some.call(mutation.removedNodes, function (node) {
          return node.id === 'search-pagination-fixer';
        });
      });

      if (fixerReplaced) {
        clearTimeout(observer.timer);
        observer.timer = setTimeout(fixPagination, 150);
      }
    });

    observer.observe(section, { childList: true, subtree: true });
  }
})();
